import React, { useState, useCallback, useMemo } from 'react';

import classNames from 'classnames';
import { useMutation, useQuery, useQueryCache } from 'react-query';
import { Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { OrbitIcons } from 'src/common/constants.';

import {
  fetchCallGroups,
  createCallGroup,
  updateCallGroup,
  deleteCallGroup,
  fetchAccountUsers,
} from '../api';
import type {
  CallGroup,
  CreateCallGroupPayload,
  InterstellarUserRow,
  UsersListResponse,
} from '../api';

interface CallGroupManagerProps {
  accountId: number;
}

interface GroupFormState {
  name: string;
  selectedUserIds: number[];
}

const userDisplayName = (u: InterstellarUserRow): string => {
  const first = u.firstName ?? u.first_name ?? '';
  const last = u.lastName ?? u.last_name ?? '';
  const full = [first, last].filter(Boolean).join(' ').trim();
  if (full) return full;
  if (u.email) return u.email.split('@')[0];
  if (u.username) return u.username;
  return `Usuario ${u.id}`;
};

export const CallGroupManager: React.FC<CallGroupManagerProps> = ({ accountId }) => {
  const queryCache = useQueryCache();
  const [editingGroup, setEditingGroup] = useState<CallGroup | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formState, setFormState] = useState<GroupFormState>({
    name: '',
    selectedUserIds: [],
  });

  const { data: groups = [], isLoading: groupsLoading } = useQuery(
    ['call-groups', accountId],
    () => fetchCallGroups(accountId),
    { refetchOnWindowFocus: false }
  );

  const { data: usersResponse, isLoading: usersLoading } = useQuery<UsersListResponse>(
    ['interstellar-account-users', accountId],
    () => fetchAccountUsers(accountId, { hydrate: false, pageSize: 200 }),
    { refetchOnWindowFocus: false }
  );

  const users = useMemo(() => usersResponse?.users ?? [], [usersResponse]);

  const resetForm = useCallback(() => {
    setFormState({ name: '', selectedUserIds: [] });
    setEditingGroup(null);
    setShowCreateForm(false);
  }, []);

  const [createGroup, { isLoading: isCreating }] = useMutation(
    (payload: CreateCallGroupPayload) => createCallGroup(accountId, payload),
    {
      onSuccess: () => {
        Toast.success('Grupo creado exitosamente');
        queryCache.invalidateQueries(['call-groups', accountId]);
        resetForm();
      },
      onError: () => {
        Toast.error('Error al crear el grupo');
      },
    }
  );

  const [updateGroup, { isLoading: isUpdating }] = useMutation(
    ({ id, payload }: { id: number; payload: CreateCallGroupPayload }) =>
      updateCallGroup(id, payload),
    {
      onSuccess: () => {
        Toast.success('Grupo actualizado exitosamente');
        queryCache.invalidateQueries(['call-groups', accountId]);
        resetForm();
      },
      onError: () => {
        Toast.error('Error al actualizar el grupo');
      },
    }
  );

  const [removeGroup] = useMutation((id: number) => deleteCallGroup(id), {
    onSuccess: () => {
      Toast.success('Grupo eliminado exitosamente');
      queryCache.invalidateQueries(['call-groups', accountId]);
    },
    onError: () => {
      Toast.error('Error al eliminar el grupo');
    },
  });

  const handleEdit = useCallback((group: CallGroup) => {
    setEditingGroup(group);
    setShowCreateForm(false);
    setFormState({
      name: group.name,
      selectedUserIds: group.participants.map((p) => p.user_id),
    });
  }, []);

  const handleCreate = useCallback(() => {
    setShowCreateForm(true);
    setEditingGroup(null);
    setFormState({ name: '', selectedUserIds: [] });
  }, []);

  const toggleUser = useCallback((userId: number) => {
    setFormState((prev) => ({
      ...prev,
      selectedUserIds: prev.selectedUserIds.includes(userId)
        ? prev.selectedUserIds.filter((id) => id !== userId)
        : [...prev.selectedUserIds, userId],
    }));
  }, []);

  const handleSave = useCallback(() => {
    const payload: CreateCallGroupPayload = {
      name: formState.name,
      users: formState.selectedUserIds,
    };

    // Editing disabled until backend PUT /group/{id} is fixed to use `users` instead of `agents`
    if (editingGroup) {
      return;
    }
    createGroup(payload);
  }, [formState, editingGroup, createGroup]);

  const usersById = useMemo(() => {
    const map = new Map<number | string, InterstellarUserRow>();
    users.forEach((u) => {
      if (u.id != null) map.set(u.id, u);
    });
    return map;
  }, [users]);

  const isFormValid = formState.name.trim().length > 0;
  const isSaving = isCreating || isUpdating;
  const isLoading = groupsLoading || usersLoading;

  return (
    <div className="p-4">
      {isLoading && <p className="text-sm text-gray-2.0">Cargando grupos...</p>}

      {!isLoading && !showCreateForm && !editingGroup && (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-2.0">
              {groups.length === 0
                ? 'No hay grupos configurados'
                : `${groups.length} grupo(s) configurado(s)`}
            </p>
            <button
              type="button"
              onClick={handleCreate}
              className="btn btn-secondary btn-sm flex items-center gap-1"
            >
              <Icon name={OrbitIcons.PlusCircle} size={14} />
              Crear grupo
            </button>
          </div>

          <div className="space-y-2">
            {groups.map((group) => {
              const { participants } = group;
              return (
                <div
                  key={group.id}
                  className="flex items-center justify-between rounded border border-white-2.0 bg-white-1.0 px-3 py-2"
                >
                  <div>
                    <p className="text-sm font-semibold text-black-1.0">{group.name}</p>
                    <p className="text-xs text-gray-2.0">
                      {participants.length} miembro(s)
                      {participants.length > 0 && (
                        <span className="ml-1">
                          (
                          {participants
                            .slice(0, 3)
                            .map((p) => {
                              const user = usersById.get(p.user_id);
                              return user ? userDisplayName(user) : `#${p.user_id}`;
                            })
                            .join(', ')}
                          {participants.length > 3 && '...'})
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleEdit(group)}
                      className="p-1 text-gray-1.0 cursor-not-allowed"
                      title="Edición de grupos próximamente (requiere actualización del backend)"
                      disabled
                    >
                      <Icon name={OrbitIcons.Pencil} size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm('¿Eliminar este grupo?')) {
                          removeGroup(group.id);
                        }
                      }}
                      className="p-1 text-gray-2.0 hover:text-red-500"
                    >
                      <Icon name={OrbitIcons.Trash} size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {(showCreateForm || editingGroup) && (
        <div className="rounded border border-white-2.0 bg-white-0.0 p-4">
          <h4 className="mb-4 text-sm font-bold text-black-1.0">
            {editingGroup ? 'Editar grupo' : 'Nuevo grupo'}
          </h4>

          <div className="mb-4">
            <label className="mb-1 block text-xs font-medium text-gray-3.0">
              Nombre del grupo<span className="ml-0.5 text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Ej: Equipo de soporte"
              value={formState.name}
              onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full rounded border border-white-2.0 px-3 py-2 text-sm"
            />
          </div>

          <div className="mb-4">
            <p className="mb-2 text-xs font-medium text-gray-3.0">
              Seleccionar miembros ({formState.selectedUserIds.length} seleccionados)
            </p>
            <div className="max-h-48 overflow-y-auto rounded border border-white-2.0 bg-white-1.0">
              {users.length === 0 ? (
                <p className="px-3 py-2 text-sm text-gray-2.0">No hay usuarios disponibles</p>
              ) : (
                users
                  .filter((u) => u.id != null && typeof u.id === 'number')
                  .map((user) => {
                    const userId = user.id as number;
                    return (
                      <label
                        key={userId}
                        className="flex cursor-pointer items-center gap-2 border-b border-white-2.0 px-3 py-2 last:border-b-0 hover:bg-white-0.0"
                      >
                        <input
                          type="checkbox"
                          checked={formState.selectedUserIds.includes(userId)}
                          onChange={() => toggleUser(userId)}
                          className="w-4 h-4 border-gray-300 rounded"
                        />
                        <span className="text-sm text-black-1.0">{userDisplayName(user)}</span>
                        {user.phone && (
                          <span className="text-xs text-gray-2.0">ext. {user.phone}</span>
                        )}
                      </label>
                    );
                  })
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={resetForm} className="btn btn-ghost btn-sm">
              Cancelar
            </button>
            <button
              type="button"
              disabled={!isFormValid || isSaving}
              onClick={handleSave}
              className="btn btn-secondary btn-sm"
            >
              {isSaving ? 'Guardando...' : editingGroup ? 'Guardar cambios' : 'Crear grupo'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
